import { WpLead, WpLeadWidget } from '../contexts/lead';
import { isAsesLeadWidget, isOksLeadWidget, isOhsLeadWidget, formatAsesLeadWidgetValue, formatOksLeadWidgetValue, formatOhsLeadWidgetValue } from './proInstrument';

export const PRO_WIDGET_LABEL = 'Patient Reported Outcome';

export type ProInstrumentId =
  | 'ases_shoulder'
  | 'oks_knee'
  | 'ohs_hip'
  | 'quickdash'
  | 'womac'
  | 'koos'
  | 'harris_hip'
  | 'vas_pain'
  | 'generic_0_10';

export const PRO_INSTRUMENT_OPTIONS: { id: ProInstrumentId; label: string }[] = [
  { id: 'ases_shoulder', label: 'ASES Shoulder Score (0–100)' },
  { id: 'oks_knee', label: 'Oxford Knee Score (0–48)' },
  { id: 'ohs_hip', label: 'Oxford Hip Score (0–48)' },
  { id: 'quickdash', label: 'QuickDASH' },
  { id: 'womac', label: 'WOMAC' },
  { id: 'koos', label: 'KOOS' },
  { id: 'harris_hip', label: 'Harris Hip Score' },
  { id: 'vas_pain', label: 'VAS Pain (0–10)' },
  { id: 'generic_0_10', label: 'Generic 0–10 PRO' },
];

export const DEFAULT_PRO_INSTRUMENT_ID: ProInstrumentId = 'ases_shoulder';

export function getProInstrumentLabel(instrumentId?: string | null): string {
  return PRO_INSTRUMENT_OPTIONS.find((option) => option.id === instrumentId)?.label ?? PRO_WIDGET_LABEL;
}

export function normalizeProInstrumentId(instrumentId?: string | null): ProInstrumentId {
  if (instrumentId && PRO_INSTRUMENT_OPTIONS.some((option) => option.id === instrumentId)) {
    return instrumentId as ProInstrumentId;
  }
  return DEFAULT_PRO_INSTRUMENT_ID;
}

export function isAiProWidget(widget: { content?: string | null }): boolean {
  return !!widget.content?.startsWith('AI PRO score');
}

export function isProWidgetLabel(label?: string | null): boolean {
  return label === PRO_WIDGET_LABEL;
}

export function isProWidget(widget: { label?: string | null; content?: string | null; widgetType?: string | null }): boolean {
  return widget.widgetType === 'PRO'
    || isProWidgetLabel(widget.label)
    || isAiProWidget(widget)
    || isAsesLeadWidget(widget)
    || isOksLeadWidget(widget)
    || isOhsLeadWidget(widget)
    || !!widget.label?.toLowerCase().includes('ases shoulder')
    || !!widget.label?.toLowerCase().includes('oxford knee')
    || !!widget.label?.toLowerCase().includes('oxford hip');
}

/** Most recent PRO widget answer for this lead, if any. */
export function findProWidgetForLead(widgets: WpLeadWidget[]): WpLeadWidget | undefined {
  const deduped = dedupeLeadWidgets(widgets ?? []);
  for (let i = deduped.length - 1; i >= 0; i--) {
    if (isProWidget(deduped[i])) return deduped[i];
  }
  return undefined;
}

export function formatProWidgetValue(
  values: string[],
  widget?: { label?: string | null; content?: string | null; values?: string[] | null },
): string {
  if (widget && isOhsLeadWidget(widget)) {
    return formatOhsLeadWidgetValue(widget);
  }
  if (widget && isOksLeadWidget(widget)) {
    return formatOksLeadWidgetValue(widget);
  }
  if (widget && isAsesLeadWidget(widget)) {
    return formatAsesLeadWidgetValue(widget);
  }
  if (!values?.length) return 'No Response';
  const score = values[0];
  if (score && /^\d+$/.test(score) && parseInt(score, 10) <= 48 && parseInt(score, 10) >= 0 && widget?.label?.toLowerCase().includes('oxford')) {
    return `${score}/48`;
  }
  if (score && /^\d+$/.test(score) && parseInt(score, 10) <= 100 && parseInt(score, 10) > 10) {
    return `${score}/100`;
  }
  return `${values[0]} / 10`;
}

export function formatWidgetResponseValue(widget: { label?: string; values?: string[]; content?: string | null }): string {
  if (!widget.values?.length && !widget.content) return 'No Response';
  if (isOhsLeadWidget(widget)) {
    return formatOhsLeadWidgetValue(widget);
  }
  if (isOksLeadWidget(widget)) {
    return formatOksLeadWidgetValue(widget);
  }
  if (isAsesLeadWidget(widget)) {
    return formatAsesLeadWidgetValue(widget);
  }
  if (isProWidgetLabel(widget.label) || isAiProWidget(widget)) {
    return formatProWidgetValue(widget.values ?? [], widget);
  }
  return widget.values?.join(', ') ?? 'No Response';
}

/** Best campaign id for generateProFromConversation — enrollment, messages, then activity. */
export function getProCampaignIdForLead(lead: WpLead): string | undefined {
  if (lead.campaigns.length) {
    for (let i = lead.campaigns.length - 1; i >= 0; i--) {
      const id = lead.campaigns[i].campaign?.id;
      if (id) return id;
    }
  }
  for (let i = lead.messages.length - 1; i >= 0; i--) {
    if (lead.messages[i].campaignId) return lead.messages[i].campaignId;
  }
  for (let i = lead.activity.length - 1; i >= 0; i--) {
    if (lead.activity[i].campaignId) return lead.activity[i].campaignId;
  }
  return undefined;
}

export function dedupeLeadWidgets(widgets: WpLeadWidget[]): WpLeadWidget[] {
  const seen = new Set<string>();
  return widgets.filter((w) => {
    if (seen.has(w.widgetId)) return false;
    seen.add(w.widgetId);
    return true;
  });
}
